import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

interface UseDesktopZoomProps {
  isMobile: boolean;
  canvasRef: React.RefObject<HTMLCanvasElement>;
}

interface UseDesktopZoomReturn {
  isZoomVisible: boolean;
  mousePosition: { x: number; y: number };
  canvasRect: DOMRect | null;
}

export const useDesktopZoom = ({ isMobile, canvasRef }: UseDesktopZoomProps): UseDesktopZoomReturn => {
  const [isZoomVisible, setIsZoomVisible] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [canvasRect, setCanvasRect] = useState<DOMRect | null>(null);

  const animationFrameId = useRef<number | null>(null);

  const handleMouseEnter = useCallback(() => {
    if (isMobile) return;
    const canvas = canvasRef.current;
    if (!canvas) return;

    setCanvasRect(canvas.getBoundingClientRect());
    setIsZoomVisible(true);
  }, [isMobile, canvasRef]);

  const handleMouseLeave = useCallback(() => {
    if (isMobile) return;

    if (animationFrameId.current) {
      cancelAnimationFrame(animationFrameId.current);
      animationFrameId.current = null;
    }

    setIsZoomVisible(false);
  }, [isMobile]);

  const handleMouseMove = useCallback(
    (event: MouseEvent) => {
      if (isMobile) return;

      if (animationFrameId.current) {
        cancelAnimationFrame(animationFrameId.current);
      }

      animationFrameId.current = requestAnimationFrame(() => {
        setMousePosition({ x: event.clientX, y: event.clientY });
      });
    },
    [isMobile]
  );

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || isMobile) return;

    canvas.addEventListener('mouseenter', handleMouseEnter);
    canvas.addEventListener('mouseleave', handleMouseLeave);
    canvas.addEventListener('mousemove', handleMouseMove);

    return () => {
      canvas.removeEventListener('mouseenter', handleMouseEnter);
      canvas.removeEventListener('mouseleave', handleMouseLeave);
      canvas.removeEventListener('mousemove', handleMouseMove);

      if (animationFrameId.current) {
        cancelAnimationFrame(animationFrameId.current);
        animationFrameId.current = null;
      }
    };
  }, [handleMouseEnter, handleMouseLeave, handleMouseMove, isMobile, canvasRef]);

  return {
    canvasRect,
    isZoomVisible,
    mousePosition,
  };
};
